// TipoParametro.Garantias no backend = 226 (enum CreditOn.Dominio.Enums.TipoParametro).
// Antes constava 222 por engano (222 = IdCliente), o que impedia o peso de casar no score.
export const TIPO_PARAMETRO_GARANTIAS = 226;

/**
 * Pesos iniciais ao incluir o critério Garantias na grade (todos editáveis).
 * O usuário define o peso máximo em cada coluna e salva na base — não usar valor fixo.
 */
export const PESO_CRITERIO_GARANTIAS_INICIAL = {
  pesoComDocumentacao: 0,
  pesoSemDocumentacao: 0,
  pesoComDocumentacaoRelacionamentoDefinido: 0,
  pesoSemDocumentacaoRelacionamentoDefinido: 0,
  indicadorEsperado: 0,
} as const;

export const CODIGO_GARANTIA_OUTROS = "Outros";

/** Separador usado nas trilhas de grupo exibidas ao usuário. */
export const SEPARADOR_CAMINHO_GARANTIA = " › ";

/**
 * Estrutura hierárquica de garantias da RDP (grupos → opções → ... → folha selecionável).
 * Só as folhas (nós sem `filhos`) são selecionáveis e possuem `codigo`.
 * Os nós intermediários servem para agrupar/navegar na UI.
 */
export interface NoGarantiaRdp {
  rotulo: string;
  /** Presente apenas em folhas (nó selecionável). */
  codigo?: string;
  filhos?: NoGarantiaRdp[];
}

export const GRUPOS_GARANTIA_RDP: NoGarantiaRdp[] = [
  {
    rotulo: "Garantia real / alienação fiduciária",
    codigo: "GarantiaRealAlienacaoFiduciaria",
  },
  {
    rotulo: "Imóvel em nome da empresa",
    filhos: [
      { rotulo: "Com ônus/gravame", codigo: "ImovelEmpresaComOnus" },
      { rotulo: "Sem ônus/gravame", codigo: "ImovelEmpresaSemOnus" },
    ],
  },
  {
    rotulo: "Carta de Fiança",
    filhos: [
      {
        rotulo: "Fiador com bens",
        filhos: [
          {
            rotulo: "Vasto patrimônio imobiliário",
            filhos: [
              { rotulo: "Livre", codigo: "FiancaFiadorBensImovelLivre" },
              { rotulo: "Com ônus", codigo: "FiancaFiadorBensImovelComOnus" },
            ],
          },
          {
            rotulo: "Aplicações financeiras",
            filhos: [
              { rotulo: "Muitas", codigo: "FiancaFiadorBensAplicacoesMuitas" },
              { rotulo: "Poucas", codigo: "FiancaFiadorBensAplicacoesPoucas" },
            ],
          },
        ],
      },
      {
        rotulo: "Fiador sem bens",
        filhos: [
          {
            rotulo: "Apenas veículos e/ou cotas sociais",
            codigo: "FiancaFiadorSemBensVeiculosCotas",
          },
        ],
      },
    ],
  },
  {
    rotulo: "Recusa em dar fiança",
    filhos: [
      {
        rotulo: "Vasto patrimônio imobiliário",
        codigo: "RecusaFiancaVastoImovel",
      },
      {
        rotulo: "Apenas veículos e/ou cotas sociais",
        codigo: "RecusaFiancaVeiculosCotas",
      },
    ],
  },
];

/** Folha selecionável já achatada, com trilha de grupos e rótulos prontos para a UI. */
export interface FolhaGarantiaRdp {
  codigo: string;
  /** Rótulo da própria folha (ex.: "Sem ônus/gravame"). */
  rotulo: string;
  /** Trilha completa de grupos até a folha, ex.: ["Carta de Fiança", "Fiador com bens", ...]. */
  trilha: string[];
  /** Grupo de topo (usado como cabeçalho no Autocomplete agrupado). */
  grupo: string;
  /** Caminho completo legível: "Carta de Fiança › Fiador com bens › ... › Livre". */
  label: string;
}

const achatarFolhas = (
  nos: NoGarantiaRdp[],
  trilhaPais: string[],
): FolhaGarantiaRdp[] =>
  nos.flatMap((no) => {
    const trilha = [...trilhaPais, no.rotulo];
    if (no.filhos && no.filhos.length > 0) {
      return achatarFolhas(no.filhos, trilha);
    }
    if (!no.codigo) return [];
    return [
      {
        codigo: no.codigo,
        rotulo: no.rotulo,
        trilha,
        grupo: trilha[0],
        label: trilha.join(SEPARADOR_CAMINHO_GARANTIA),
      },
    ];
  });

/** Lista achatada de todas as folhas selecionáveis (substitui a antiga TIPOS_GARANTIA_RDP). */
export const FOLHAS_GARANTIA_RDP: FolhaGarantiaRdp[] = achatarFolhas(
  GRUPOS_GARANTIA_RDP,
  [],
);

/**
 * Lista de tipos no formato {codigo, label} mantido por compatibilidade com a UI antiga.
 * Agora o label é o caminho completo da folha.
 */
export const TIPOS_GARANTIA_RDP = FOLHAS_GARANTIA_RDP.map((f) => ({
  codigo: f.codigo,
  label: f.label,
}));

export const COMPOSICOES_GARANTIA_PADRAO_RDP: {
  codigos: string[];
  fator: number;
  descricao: string;
}[] = [
  { codigos: ["GarantiaRealAlienacaoFiduciaria"], fator: 1, descricao: "Garantia real / alienação fiduciária" },
  { codigos: ["ImovelEmpresaSemOnus"], fator: 0.75, descricao: "Imóvel em nome da empresa - Sem ônus/gravame" },
  { codigos: ["ImovelEmpresaComOnus"], fator: 0.25, descricao: "Imóvel em nome da empresa - Com ônus/gravame" },
  { codigos: ["FiancaFiadorBensImovelLivre"], fator: 0.75, descricao: "Fiança > Fiador com bens > Vasto patrimônio imobiliário > Livre" },
  { codigos: ["FiancaFiadorBensImovelComOnus"], fator: 0.25, descricao: "Fiança > Fiador com bens > Vasto patrimônio imobiliário > Com ônus" },
  { codigos: ["FiancaFiadorBensAplicacoesMuitas"], fator: 0.65, descricao: "Fiança > Fiador com bens > Aplicações financeiras > Muitas" },
  { codigos: ["FiancaFiadorBensAplicacoesPoucas"], fator: 0.25, descricao: "Fiança > Fiador com bens > Aplicações financeiras > Poucas" },
  { codigos: ["FiancaFiadorSemBensVeiculosCotas"], fator: 0.2, descricao: "Fiança > Fiador sem bens > Apenas veículos e/ou cotas sociais" },
  { codigos: ["RecusaFiancaVastoImovel"], fator: 0.35, descricao: "Recusa em dar fiança > Vasto patrimônio imobiliário" },
  { codigos: ["RecusaFiancaVeiculosCotas"], fator: 0, descricao: "Recusa em dar fiança > Apenas veículos e/ou cotas sociais" },
];

export const montarChaveCombinacaoGarantia = (codigos: string[]) =>
  // dedup + ordenação alfabética para casar exatamente com a chave gerada no backend
  // (GarantiaComposicaoHelper.MontarChaveCombinacao usa Distinct + OrderBy).
  Array.from(new Set([...codigos].filter(Boolean)))
    .sort((a, b) => a.localeCompare(b))
    .join("|");

export const parseChaveCombinacaoGarantia = (chave: string) =>
  chave ? chave.split("|").filter(Boolean) : [];

export const getLabelTipoGarantia = (codigo: string) =>
  FOLHAS_GARANTIA_RDP.find((t) => t.codigo === codigo)?.label ?? codigo;

/** Grupo de topo de uma folha (cabeçalho da seção no Autocomplete agrupado). */
export const getGrupoTipoGarantia = (codigo: string) =>
  FOLHAS_GARANTIA_RDP.find((t) => t.codigo === codigo)?.grupo ?? "";

export const mapLabelsParaCodigosGarantia = (selecionados: string[]) =>
  selecionados.map((item) => {
    const porCodigo = FOLHAS_GARANTIA_RDP.find((t) => t.codigo === item);
    if (porCodigo) return porCodigo.codigo;
    const porLabel = FOLHAS_GARANTIA_RDP.find((t) => t.label === item);
    return porLabel?.codigo ?? item;
  });

export const isCriterioGarantias = (tipoParametro: string | number) =>
  Number(tipoParametro) === TIPO_PARAMETRO_GARANTIAS;

export interface ComposicaoGarantiaValidavel {
  chaveCombinacao: string;
  fator: number;
  ativo: boolean;
}

/** Tipos que aparecem em ao menos uma composição ativa cadastrada na parametrização. */
export const extrairTiposDisponiveisDasComposicoes = (
  composicoes: ComposicaoGarantiaValidavel[],
): string[] => {
  const codigosSet = new Set<string>();
  composicoes
    .filter((c) => c.ativo && c.chaveCombinacao)
    .forEach((c) => {
      parseChaveCombinacaoGarantia(c.chaveCombinacao).forEach((codigo) =>
        codigosSet.add(codigo),
      );
    });
  return FOLHAS_GARANTIA_RDP.filter((t) => codigosSet.has(t.codigo)).map(
    (t) => t.codigo,
  );
};

/** Localiza a composição ativa cuja chave bate exatamente com os tipos selecionados. */
export const buscarComposicaoAtivaPorTipos = (
  composicoes: ComposicaoGarantiaValidavel[],
  tiposCodigo: string[],
) => {
  const chave = montarChaveCombinacaoGarantia(tiposCodigo);
  return composicoes.find((c) => c.ativo && c.chaveCombinacao === chave);
};

export const validarComposicoesGarantia = (
  composicoes: ComposicaoGarantiaValidavel[],
): { valido: boolean; erros: string[] } => {
  const erros: string[] = [];
  const ativas = composicoes.filter((c) => c.ativo);
  const chavesVistas = new Set<string>();

  composicoes.forEach((comp, index) => {
    const linha = index + 1;
    if (!comp.ativo) return;

    const tipos = parseChaveCombinacaoGarantia(comp.chaveCombinacao);
    if (tipos.length === 0) {
      erros.push(`Linha ${linha}: selecione ao menos um tipo de garantia.`);
      return;
    }

    if (comp.fator < 0 || comp.fator > 1 || Number.isNaN(comp.fator)) {
      erros.push(`Linha ${linha}: fator deve estar entre 0 e 1.`);
    }

    if (chavesVistas.has(comp.chaveCombinacao)) {
      erros.push(
        `Combinação duplicada: ${tipos.map(getLabelTipoGarantia).join(" + ")}.`,
      );
    }
    chavesVistas.add(comp.chaveCombinacao);
  });

  if (ativas.length === 0) {
    erros.push("Cadastre ao menos uma composição de garantia ativa.");
  }

  return { valido: erros.length === 0, erros };
};

export const formatarNotaGarantias = (nota?: number) => {
  if (nota === undefined || nota === null || Number.isNaN(nota)) return "—";
  return Number(nota).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};
